import React from 'react';
import { useSelector } from 'react-redux';
import { Box, styled } from '@mui/system';
import { Avatar, ButtonBase } from '@mui/material';
import { selectContext, selectContextStatus } from '../redux/features/context/contextSlice';
import useFacetFilters, { GRID_CHIP_KINDS } from '../hooks/useFacetFilters';
import useZenMode from '../hooks/useZenMode';
import {
  getAuthorImageUrl, getAuthorName, getSiteTitle, mergePublicAndPrivateItems,
} from '../common/ItemTools';
import { buildMediaTypeNameMap, parseMediaType } from '../common/MediaTypes';
import { BAR_BG, BAR_BORDER, BAR_TEXT_MUTED } from './common/tokens';

const MEDIA_TYPE_NAMES = buildMediaTypeNameMap();

// The reader-facing name of each named view.
const VIEW_LABELS = {
  favorites: 'favorites',
  history: 'already reviewed',
  unknown: 'unknown type',
  metareview: 'metadata not reviewed',
};

// Resolve a chip's display label from the loaded entity maps.
// `resolved` is false while the name is still missing.
function facetLabel({ kind, value }, context) {
  switch (kind) {
    case 'author': {
      const author = context?.authors?.[value];
      const name = getAuthorName(author);
      const imageUrl = getAuthorImageUrl(author?.privateData, author?.imageScope)
        || getAuthorImageUrl(author?.publicData, 'public');
      return {
        label: `author: ${name || value}`, resolved: !!name, imageUrl, name,
      };
    }
    case 'site': {
      const title = getSiteTitle(context?.sites?.[value]);
      return { label: `site: ${title || value}`, resolved: !!title };
    }
    case 'list': {
      const title = mergePublicAndPrivateItems(context?.lists?.[value])?.title;
      return { label: `list: ${title || value}`, resolved: !!title };
    }
    case 'tag': {
      const tag = context?.tags?.privateData?.[value] || context?.tags?.publicData?.[value];
      return { label: `#${tag?.name || value}`, resolved: !!tag?.name };
    }
    case 'type':
      return { label: `type: ${(MEDIA_TYPE_NAMES[parseMediaType(value)]?.label || value).toLowerCase()}`, resolved: true };
    case 'view':
      return { label: `view: ${VIEW_LABELS[value] || value}`, resolved: true };
    default:
      return { label: value, resolved: true };
  }
}

// Two-letter monogram for an author without a picture.
// Single-word names take their first two letters; multi-word names take the
// first letter of each.
function authorInitials(name) {
  if (!name) return '?';
  const clean = name.replace(/^@/, '').trim();
  const parts = clean.split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

// The white overlays and the label grey are this bar's own - the rest of the
// dark chrome comes from the shared bar tokens.
const COLORS = {
  chipBg: 'rgba(255,255,255,0.08)',
  chipBorder: 'rgba(255,255,255,0.18)',
  label: '#b8b8b8',
};

const Bar = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  background: BAR_BG,
  borderBottom: `1px solid ${BAR_BORDER}`,
  padding: '7px 16px',
  minHeight: 40,
  flexShrink: 0,
});

const FilteringByLabel = styled('span')({
  fontSize: 14,
  color: COLORS.label,
  flexShrink: 0,
});

// One filter chip. Author chips seat an 18px avatar (picture or initials) on the
// left, so they use a tighter left pad (4px vs 9px).
const Chip = styled('span', {
  shouldForwardProp: (prop) => prop !== 'author',
})(({ author, theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  background: theme.palette.primary.main,
  border: `1px solid ${theme.palette.primary.main}`,
  borderRadius: 8,
  padding: `3px 4px 3px ${author ? 4 : 9}px`,
  color: '#fff',
  fontSize: 13,
  fontWeight: 600,
  whiteSpace: 'nowrap',
}));

const RemoveButton = styled(ButtonBase)(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 18,
  height: 18,
  borderRadius: '50%',
  color: '#fff',
  flexShrink: 0,
  '&:hover': {
    background: 'rgba(255,255,255,0.25)',
  },
}));

const ClearAllButton = styled(ButtonBase)(({ theme }) => ({
  fontSize: 13,
  fontFamily: 'inherit',
  color: BAR_TEXT_MUTED,
  textDecoration: 'underline',
  textUnderlineOffset: 3,
  flexShrink: 0,
  '&:hover': { color: theme.palette.primary.main },
}));

function CloseIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

/**
 * Bar of active filter chips, rendered above an entity grid. Each chip - the
 * named view included - can be removed individually or all at once. The bar is
 * absent when no chip is active, so it never occupies space on a plain grid.
 * `entity` names the grid below, which decides the chips it can show.
 */
export default function FilterBar({ entity = 'items' }) {
  const { activeFilters, removeFilter, clearFilters } = useFacetFilters();
  const context = useSelector(selectContext);
  const status = useSelector(selectContextStatus);
  const zenMode = useZenMode();
  const loading = status === 'idle' || status === 'loading';

  if (zenMode) return null;

  const kinds = GRID_CHIP_KINDS[entity] || GRID_CHIP_KINDS.items;
  const chips = activeFilters
    .filter((filter) => kinds.includes(filter.kind))
    .map((filter) => ({ ...filter, ...facetLabel(filter, context) }));

  if (!chips.length) return null;

  // On a cold load the entity maps aren't populated yet. Once loading settles
  // the bar always renders, so a chip whose name can't be resolved
  // (rare, e.g. a deleted entity) still shows its id and stays removable.
  if (loading && chips.some((chip) => !chip.resolved)) return null;

  return (
    <Bar>
      <FilteringByLabel>Filtering by</FilteringByLabel>

      {chips.map((chip) => (
        <Chip key={chip.id} author={chip.kind === 'author' ? 1 : 0}>
          {chip.kind === 'author' && (
            <Avatar
              src={chip.imageUrl || undefined}
              alt=""
              sx={{
                width: 18,
                height: 18,
                fontSize: 9,
                fontWeight: 700,
                bgcolor: '#fff',
                color: 'primary.main',
              }}
            >
              {authorInitials(chip.name)}
            </Avatar>
          )}
          {chip.label}
          <RemoveButton
            onClick={() => removeFilter(chip.id)}
            aria-label={`Remove filter ${chip.label}`}
          >
            <CloseIcon />
          </RemoveButton>
        </Chip>
      ))}

      <Box sx={{ flex: 1 }} />

      <ClearAllButton onClick={clearFilters}>clear all</ClearAllButton>
    </Bar>
  );
}
